import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster, TooltipProvider } from '@hemmafixa/ui';
import { useState, type ReactNode } from 'react';
import { sessionKey } from '@/features/auth/api';
import { I18nProvider } from '@/i18n/provider';
import { ApiError } from '@/lib/api';

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => {
    // A 401 that survived the API client's refresh means the session has ended (logged out elsewhere,
    // password changed, account suspended): forget the user, and the protected page sends them to login.
    const onError = (error: unknown) => {
      if (error instanceof ApiError && error.status === 401) client.setQueryData(sessionKey, null);
    };
    const client: QueryClient = new QueryClient({
      queryCache: new QueryCache({ onError }),
      mutationCache: new MutationCache({ onError }),
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          refetchOnWindowFocus: false,
          // Don't retry what will fail again (validation, auth, not found)
          retry: (failureCount, error) =>
            !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 2,
        },
      },
    });
    return client;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <TooltipProvider delayDuration={300}>
          {children}
          <Toaster />
        </TooltipProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
